import { useEffect, useState } from "react";
import { Lock, RefreshCw, Unlock } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/auth";
import {
  CURRENCY,
  PERIOD_STATUS_BADGE,
  monthName,
  type GlPeriod,
  type GlTrialBalanceResponse,
} from "@/lib/gl";

function fmt(n: number) {
  if (Math.abs(n) < 0.005) return "—";
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function GeneralLedgerPeriods() {
  const { can, user } = useAuth();
  const canClose = can("gl.close");
  const isAdmin = user?.roles.includes("administrator") ?? false;

  const [periods, setPeriods] = useState<GlPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const pg = usePagination(periods);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods]);

  const [closeOpen, setCloseOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<GlPeriod | null>(null);
  const [tb, setTb] = useState<GlTrialBalanceResponse | null>(null);
  const [closing, setClosing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ periods: GlPeriod[] }>("/gl/periods");
      setPeriods(d.periods);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load periods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openClose = async (p: GlPeriod) => {
    setCloseTarget(p);
    setTb(null);
    setCloseOpen(true);
    try {
      const d = await api.get<GlTrialBalanceResponse>(
        `/gl/reports/trial-balance?as_of=${p.end_date}`,
      );
      setTb(d);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load pre-close TB");
    }
  };

  const doClose = async () => {
    if (!closeTarget) return;
    if (!confirm(`Close ${monthName(closeTarget.month)} ${closeTarget.year}? Journals dated in this period will be locked from posting.`)) return;
    setClosing(true);
    try {
      await api.post("/gl/periods/close", {
        year: closeTarget.year,
        month: closeTarget.month,
      });
      toast.success(`Closed ${monthName(closeTarget.month)} ${closeTarget.year}`);
      setCloseOpen(false);
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as { error?: string } | null)?.error ?? e.message)
          : (e as Error).message;
      toast.error(msg);
    } finally {
      setClosing(false);
    }
  };

  const reopen = async (p: GlPeriod) => {
    if (!confirm(`Reopen ${monthName(p.month)} ${p.year}? Only administrators can do this.`)) return;
    try {
      await api.post("/gl/periods/reopen", { year: p.year, month: p.month });
      toast.success(`Reopened ${monthName(p.month)} ${p.year}`);
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as { error?: string } | null)?.error ?? e.message)
          : (e as Error).message;
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">Accounting Periods</CardTitle>
            <CardDescription>
              Months are auto-created on first journal posting. Close a period
              to lock further postings.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Range</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : periods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No periods yet.
                  </TableCell>
                </TableRow>
              ) : (
                pg.pageItems.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {monthName(p.month)} {p.year}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.start_date} → {p.end_date}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`capitalize ${PERIOD_STATUS_BADGE[p.status] ?? ""}`}
                      >
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.closed_at ? formatDateTime(p.closed_at) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {p.status === "open" && canClose && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openClose(p)}
                        >
                          <Lock className="h-4 w-4 mr-1" />
                          Close
                        </Button>
                      )}
                      {p.status === "closed" && isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => reopen(p)}
                        >
                          <Unlock className="h-4 w-4 mr-1" />
                          Reopen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!loading && periods.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Close{" "}
              {closeTarget
                ? `${monthName(closeTarget.month)} ${closeTarget.year}`
                : "Period"}
            </DialogTitle>
            <DialogDescription>
              Review the pre-close trial balance. Closing locks all postings
              dated within this period.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!tb ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      Loading trial balance…
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {tb.rows.map((r) => (
                      <TableRow key={r.account_id}>
                        <TableCell className="font-mono">{r.code}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {fmt(r.debit)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {fmt(r.credit)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/40 font-semibold">
                      <TableCell colSpan={2} className="text-right">
                        Totals
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {fmt(tb.total_debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {fmt(tb.total_credit)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>
              Cancel
            </Button>
            <Button onClick={doClose} disabled={closing || !tb}>
              {closing ? "Closing…" : "Confirm Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
